/**
 * Pre-built Certificate Template Designs
 * These are starter templates users can select and customize
 */

import {
  CanvasLayoutData,
  CanvasElementType,
  DEFAULT_TEXT_STYLE,
  generateElementId
} from '@/types/certificate-template.types';

export interface TemplatePreset {
  id: string;
  name: string;
  description: string;
  category: 'elegant' | 'modern' | 'classic' | 'minimalist' | 'professional' | 'colorful';
  thumbnail?: string;
  tags: string[];
  layout: CanvasLayoutData;
}

// Helper to create element IDs
const el = () => generateElementId();

/**
 * Elegant Gold Certificate Template
 */
export const elegantGoldTemplate: TemplatePreset = {
  id: 'elegant-gold',
  name: 'Elegant Gold Classic',
  description: 'A sophisticated gold-themed certificate with elegant typography',
  category: 'elegant',
  tags: ['gold', 'elegant', 'classic', 'professional'],
  layout: {
    version: '1.0.0',
    canvas: {
      width: 842,
      height: 595,
      orientation: 'landscape',
      background: {
        type: 'solid',
        color: '#FFFEF7'
      },
      border: {
        width: 8,
        style: 'solid',
        color: '#D4AF37',
        radius: 0
      },
      padding: 40
    },
    elements: [
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Inner Border',
        shapeType: 'rectangle',
        fill: 'transparent',
        border: { width: 2, style: 'solid', color: '#D4AF37', radius: 0 },
        position: { x: 20, y: 20, width: 802, height: 555, zIndex: 1 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Certificate Title',
        content: 'CERTIFICATE',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: 48,
          fontWeight: 'bold',
          letterSpacing: 8,
          color: '#D4AF37'
        },
        position: { x: 221, y: 60, width: 400, height: 60, zIndex: 2 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Of Completion',
        content: 'OF COMPLETION',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: 18,
          fontWeight: 'normal',
          letterSpacing: 4,
          color: '#666666'
        },
        position: { x: 296, y: 115, width: 250, height: 30, zIndex: 3 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.LINE,
        name: 'Decorative Line Top',
        strokeWidth: 2,
        strokeColor: '#D4AF37',
        strokeStyle: 'solid',
        startPoint: { x: 0, y: 0 },
        endPoint: { x: 200, y: 0 },
        position: { x: 321, y: 155, width: 200, height: 4, zIndex: 4 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Proudly Presented',
        content: 'This certificate is proudly presented to',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 14,
          fontStyle: 'italic',
          color: '#666666'
        },
        position: { x: 246, y: 175, width: 350, height: 25, zIndex: 5 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Recipient Name',
        variableKey: 'holder_name',
        placeholder: 'John Doe',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Dancing Script", cursive',
          fontSize: 42,
          fontWeight: 'bold',
          color: '#1a1a1a'
        },
        position: { x: 171, y: 205, width: 500, height: 55, zIndex: 6 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.LINE,
        name: 'Name Underline',
        strokeWidth: 1,
        strokeColor: '#D4AF37',
        strokeStyle: 'solid',
        startPoint: { x: 0, y: 0 },
        endPoint: { x: 400, y: 0 },
        position: { x: 221, y: 260, width: 400, height: 4, zIndex: 7 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'For Completing',
        content: 'for successfully completing',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 14,
          color: '#666666'
        },
        position: { x: 296, y: 275, width: 250, height: 25, zIndex: 8 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Course Title',
        variableKey: 'course_title',
        placeholder: 'Advanced Web Development',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 24,
          fontWeight: 'bold',
          color: '#333333'
        },
        position: { x: 121, y: 305, width: 600, height: 40, zIndex: 9 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Issue Date',
        variableKey: 'issued_date',
        placeholder: 'January 15, 2024',
        format: 'date',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 12,
          color: '#666666'
        },
        position: { x: 100, y: 480, width: 150, height: 25, zIndex: 10 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Date Label',
        content: 'Date',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 10,
          color: '#999999',
          letterSpacing: 1
        },
        position: { x: 100, y: 505, width: 150, height: 20, zIndex: 11 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SIGNATURE,
        name: 'Signature',
        signatureType: 'text',
        text: 'Jane Smith',
        showLabel: true,
        label: 'Director of Education',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Dancing Script", cursive',
          fontSize: 28,
          color: '#333333'
        },
        position: { x: 546, y: 450, width: 200, height: 80, zIndex: 12 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Certificate ID',
        variableKey: 'certificate_number',
        placeholder: 'CERT-2024-001234',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          color: '#999999'
        },
        position: { x: 321, y: 550, width: 200, height: 20, zIndex: 13 },
        locked: false,
        visible: true,
        opacity: 1
      }
    ],
    gridEnabled: true,
    snapToGrid: true,
    gridSize: 10
  }
};

/**
 * Modern Blue Certificate Template
 */
export const modernBlueTemplate: TemplatePreset = {
  id: 'modern-blue',
  name: 'Modern Blue Professional',
  description: 'A clean, modern design with blue accents',
  category: 'modern',
  tags: ['blue', 'modern', 'clean', 'professional'],
  layout: {
    version: '1.0.0',
    canvas: {
      width: 842,
      height: 595,
      orientation: 'landscape',
      background: {
        type: 'solid',
        color: '#FFFFFF'
      },
      border: {
        width: 0,
        style: 'solid',
        color: 'transparent',
        radius: 0
      },
      padding: 40
    },
    elements: [
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Blue Header Bar',
        shapeType: 'rectangle',
        fill: '#1E40AF',
        position: { x: 0, y: 0, width: 842, height: 120, zIndex: 1 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Gold Accent',
        shapeType: 'rectangle',
        fill: '#F59E0B',
        position: { x: 0, y: 120, width: 842, height: 6, zIndex: 2 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Certificate Title',
        content: 'CERTIFICATE',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 36,
          fontWeight: 'bold',
          letterSpacing: 6,
          color: '#FFFFFF'
        },
        position: { x: 221, y: 35, width: 400, height: 50, zIndex: 3 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Of Achievement',
        content: 'OF ACHIEVEMENT',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 14,
          fontWeight: 'normal',
          letterSpacing: 3,
          color: '#93C5FD'
        },
        position: { x: 296, y: 80, width: 250, height: 25, zIndex: 4 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Presented To',
        content: 'THIS CERTIFICATE IS PRESENTED TO',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 11,
          fontWeight: 'normal',
          letterSpacing: 2,
          color: '#6B7280'
        },
        position: { x: 246, y: 160, width: 350, height: 20, zIndex: 5 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Recipient Name',
        variableKey: 'holder_name',
        placeholder: 'John Doe',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 38,
          fontWeight: 'bold',
          color: '#1E40AF'
        },
        position: { x: 121, y: 190, width: 600, height: 50, zIndex: 6 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Underline',
        shapeType: 'rectangle',
        fill: '#F59E0B',
        position: { x: 271, y: 245, width: 300, height: 3, zIndex: 7 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'For Completing',
        content: 'For successfully completing the course',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 12,
          color: '#6B7280'
        },
        position: { x: 271, y: 265, width: 300, height: 20, zIndex: 8 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Course Title',
        variableKey: 'course_title',
        placeholder: 'Advanced Web Development Masterclass',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 20,
          fontWeight: '600',
          color: '#111827'
        },
        position: { x: 121, y: 295, width: 600, height: 35, zIndex: 9 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Footer Bar',
        shapeType: 'rectangle',
        fill: '#F3F4F6',
        position: { x: 0, y: 450, width: 842, height: 145, zIndex: 10 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Issue Date',
        variableKey: 'issued_date',
        placeholder: 'January 15, 2024',
        format: 'date',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 14,
          fontWeight: '600',
          color: '#374151'
        },
        position: { x: 80, y: 490, width: 180, height: 25, zIndex: 11 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Date Label',
        content: 'DATE ISSUED',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 10,
          letterSpacing: 1,
          color: '#9CA3AF'
        },
        position: { x: 80, y: 515, width: 180, height: 20, zIndex: 12 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SIGNATURE,
        name: 'Signature',
        signatureType: 'text',
        text: 'Michael Johnson',
        showLabel: true,
        label: 'CEO & Founder',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Dancing Script", cursive',
          fontSize: 24,
          color: '#374151'
        },
        position: { x: 571, y: 470, width: 200, height: 70, zIndex: 13 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Certificate ID',
        variableKey: 'certificate_number',
        placeholder: 'CERT-2024-001234',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          color: '#9CA3AF'
        },
        position: { x: 321, y: 560, width: 200, height: 20, zIndex: 14 },
        locked: false,
        visible: true,
        opacity: 1
      }
    ],
    gridEnabled: true,
    snapToGrid: true,
    gridSize: 10
  }
};

/**
 * Minimalist White Certificate Template
 */
export const minimalistWhiteTemplate: TemplatePreset = {
  id: 'minimalist-white',
  name: 'Minimalist Clean',
  description: 'A simple, clean design with minimal elements',
  category: 'minimalist',
  tags: ['minimalist', 'clean', 'simple', 'white'],
  layout: {
    version: '1.0.0',
    canvas: {
      width: 842,
      height: 595,
      orientation: 'landscape',
      background: {
        type: 'solid',
        color: '#FFFFFF'
      },
      border: {
        width: 1,
        style: 'solid',
        color: '#E5E7EB',
        radius: 0
      },
      padding: 60
    },
    elements: [
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Certificate Title',
        content: 'Certificate of Completion',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 32,
          fontWeight: 'normal',
          color: '#111827'
        },
        position: { x: 171, y: 100, width: 500, height: 45, zIndex: 1 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.LINE,
        name: 'Simple Line',
        strokeWidth: 1,
        strokeColor: '#D1D5DB',
        strokeStyle: 'solid',
        startPoint: { x: 0, y: 0 },
        endPoint: { x: 100, y: 0 },
        position: { x: 371, y: 160, width: 100, height: 4, zIndex: 2 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Presented To',
        content: 'Presented to',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 14,
          color: '#6B7280'
        },
        position: { x: 321, y: 185, width: 200, height: 25, zIndex: 3 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Recipient Name',
        variableKey: 'holder_name',
        placeholder: 'John Doe',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 36,
          fontWeight: 'normal',
          color: '#111827'
        },
        position: { x: 171, y: 220, width: 500, height: 50, zIndex: 4 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'For Completing',
        content: 'for completing',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 14,
          color: '#6B7280'
        },
        position: { x: 346, y: 280, width: 150, height: 25, zIndex: 5 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Course Title',
        variableKey: 'course_title',
        placeholder: 'Introduction to Design Thinking',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 20,
          fontWeight: 'normal',
          fontStyle: 'italic',
          color: '#374151'
        },
        position: { x: 171, y: 315, width: 500, height: 35, zIndex: 6 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Issue Date',
        variableKey: 'issued_date',
        placeholder: 'January 15, 2024',
        format: 'date',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 12,
          color: '#9CA3AF'
        },
        position: { x: 80, y: 500, width: 150, height: 20, zIndex: 7 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SIGNATURE,
        name: 'Signature',
        signatureType: 'text',
        text: 'Sarah Williams',
        showLabel: true,
        label: 'Program Director',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 18,
          fontStyle: 'italic',
          color: '#374151'
        },
        position: { x: 571, y: 470, width: 200, height: 70, zIndex: 8 },
        locked: false,
        visible: true,
        opacity: 1
      }
    ],
    gridEnabled: true,
    snapToGrid: true,
    gridSize: 10
  }
};

/**
 * Classic Vintage Certificate Template
 */
export const classicVintageTemplate: TemplatePreset = {
  id: 'classic-vintage',
  name: 'Classic Vintage',
  description: 'A traditional vintage-style certificate with ornate details',
  category: 'classic',
  tags: ['vintage', 'classic', 'traditional', 'ornate'],
  layout: {
    version: '1.0.0',
    canvas: {
      width: 842,
      height: 595,
      orientation: 'landscape',
      background: {
        type: 'solid',
        color: '#FDF6E3'
      },
      border: {
        width: 12,
        style: 'solid',
        color: '#8B4513',
        radius: 0
      },
      padding: 40
    },
    elements: [
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Inner Border',
        shapeType: 'rectangle',
        fill: 'transparent',
        border: { width: 3, style: 'solid', color: '#8B4513', radius: 0 },
        position: { x: 25, y: 25, width: 792, height: 545, zIndex: 1 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Certificate Title',
        content: 'Certificate',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 56,
          fontWeight: 'bold',
          fontStyle: 'italic',
          color: '#8B4513'
        },
        position: { x: 221, y: 50, width: 400, height: 70, zIndex: 2 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Of Achievement',
        content: '~ of Achievement ~',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 18,
          fontStyle: 'italic',
          color: '#A0522D'
        },
        position: { x: 296, y: 115, width: 250, height: 30, zIndex: 3 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Presented To',
        content: 'This is to certify that',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 14,
          fontStyle: 'italic',
          color: '#666666'
        },
        position: { x: 296, y: 170, width: 250, height: 25, zIndex: 4 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Recipient Name',
        variableKey: 'holder_name',
        placeholder: 'John Doe',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Great Vibes", cursive',
          fontSize: 48,
          color: '#2C1810'
        },
        position: { x: 121, y: 200, width: 600, height: 60, zIndex: 5 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.LINE,
        name: 'Decorative Line',
        strokeWidth: 2,
        strokeColor: '#8B4513',
        strokeStyle: 'solid',
        startPoint: { x: 0, y: 0 },
        endPoint: { x: 350, y: 0 },
        position: { x: 246, y: 265, width: 350, height: 4, zIndex: 6 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Has Completed',
        content: 'has successfully completed the requirements for',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 13,
          color: '#666666'
        },
        position: { x: 221, y: 280, width: 400, height: 25, zIndex: 7 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Course Title',
        variableKey: 'course_title',
        placeholder: 'Master of Business Administration',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 22,
          fontWeight: 'bold',
          color: '#2C1810'
        },
        position: { x: 121, y: 310, width: 600, height: 35, zIndex: 8 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Issue Date',
        variableKey: 'issued_date',
        placeholder: 'January 15, 2024',
        format: 'date',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 12,
          color: '#8B4513'
        },
        position: { x: 100, y: 490, width: 150, height: 25, zIndex: 9 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Date Label',
        content: 'Given this day',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Georgia, serif',
          fontSize: 10,
          fontStyle: 'italic',
          color: '#A0522D'
        },
        position: { x: 100, y: 515, width: 150, height: 20, zIndex: 10 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SIGNATURE,
        name: 'Signature',
        signatureType: 'text',
        text: 'Robert Anderson',
        showLabel: true,
        label: 'Dean of Studies',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Great Vibes", cursive',
          fontSize: 28,
          color: '#2C1810'
        },
        position: { x: 546, y: 460, width: 220, height: 80, zIndex: 11 },
        locked: false,
        visible: true,
        opacity: 1
      }
    ],
    gridEnabled: true,
    snapToGrid: true,
    gridSize: 10
  }
};

/**
 * Professional Corporate Template
 */
export const professionalCorporateTemplate: TemplatePreset = {
  id: 'professional-corporate',
  name: 'Professional Corporate',
  description: 'A formal corporate-style certificate for professional settings',
  category: 'professional',
  tags: ['corporate', 'professional', 'formal', 'business'],
  layout: {
    version: '1.0.0',
    canvas: {
      width: 842,
      height: 595,
      orientation: 'landscape',
      background: {
        type: 'solid',
        color: '#FFFFFF'
      },
      border: {
        width: 2,
        style: 'solid',
        color: '#1F2937',
        radius: 0
      },
      padding: 40
    },
    elements: [
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Top Accent',
        shapeType: 'rectangle',
        fill: '#1F2937',
        position: { x: 0, y: 0, width: 842, height: 8, zIndex: 1 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Teal Accent',
        shapeType: 'rectangle',
        fill: '#0D9488',
        position: { x: 0, y: 8, width: 842, height: 4, zIndex: 2 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Company Name',
        content: 'SKILLZEN ACADEMY',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Arial, sans-serif',
          fontSize: 12,
          fontWeight: 'bold',
          letterSpacing: 3,
          color: '#6B7280'
        },
        position: { x: 321, y: 40, width: 200, height: 20, zIndex: 3 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Certificate Title',
        content: 'CERTIFICATE OF ACHIEVEMENT',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Arial, sans-serif',
          fontSize: 28,
          fontWeight: 'bold',
          letterSpacing: 2,
          color: '#1F2937'
        },
        position: { x: 146, y: 70, width: 550, height: 40, zIndex: 4 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.LINE,
        name: 'Header Line',
        strokeWidth: 2,
        strokeColor: '#0D9488',
        strokeStyle: 'solid',
        startPoint: { x: 0, y: 0 },
        endPoint: { x: 200, y: 0 },
        position: { x: 321, y: 120, width: 200, height: 4, zIndex: 5 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Presented To',
        content: 'This certificate is awarded to',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Arial, sans-serif',
          fontSize: 13,
          color: '#6B7280'
        },
        position: { x: 271, y: 145, width: 300, height: 25, zIndex: 6 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Recipient Name',
        variableKey: 'holder_name',
        placeholder: 'John Doe',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Arial, sans-serif',
          fontSize: 36,
          fontWeight: 'bold',
          color: '#0D9488'
        },
        position: { x: 121, y: 175, width: 600, height: 50, zIndex: 7 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'In Recognition',
        content: 'in recognition of successfully completing',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Arial, sans-serif',
          fontSize: 13,
          color: '#6B7280'
        },
        position: { x: 246, y: 235, width: 350, height: 25, zIndex: 8 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Course Title',
        variableKey: 'course_title',
        placeholder: 'Professional Leadership Development',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Arial, sans-serif',
          fontSize: 22,
          fontWeight: '600',
          color: '#1F2937'
        },
        position: { x: 121, y: 265, width: 600, height: 35, zIndex: 9 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Footer Section',
        shapeType: 'rectangle',
        fill: '#F9FAFB',
        position: { x: 0, y: 430, width: 842, height: 165, zIndex: 10 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Issue Date',
        variableKey: 'issued_date',
        placeholder: 'January 15, 2024',
        format: 'date',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Arial, sans-serif',
          fontSize: 14,
          fontWeight: '600',
          color: '#374151'
        },
        position: { x: 100, y: 480, width: 180, height: 25, zIndex: 11 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Date Label',
        content: 'DATE',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Arial, sans-serif',
          fontSize: 10,
          fontWeight: 'bold',
          letterSpacing: 1,
          color: '#9CA3AF'
        },
        position: { x: 100, y: 505, width: 180, height: 20, zIndex: 12 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SIGNATURE,
        name: 'Signature',
        signatureType: 'text',
        text: 'David Miller',
        showLabel: true,
        label: 'Chief Learning Officer',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Dancing Script", cursive',
          fontSize: 24,
          color: '#374151'
        },
        position: { x: 571, y: 460, width: 200, height: 70, zIndex: 13 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Certificate ID',
        variableKey: 'certificate_number',
        placeholder: 'ID: CERT-2024-001234',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Courier New", monospace',
          fontSize: 10,
          color: '#9CA3AF'
        },
        position: { x: 321, y: 560, width: 200, height: 20, zIndex: 14 },
        locked: false,
        visible: true,
        opacity: 1
      }
    ],
    gridEnabled: true,
    snapToGrid: true,
    gridSize: 10
  }
};

/**
 * Colorful Achievement Template
 */
export const colorfulAchievementTemplate: TemplatePreset = {
  id: 'colorful-achievement',
  name: 'Colorful Achievement',
  description: 'A vibrant, colorful design for achievements and recognition',
  category: 'colorful',
  tags: ['colorful', 'vibrant', 'achievement', 'fun'],
  layout: {
    version: '1.0.0',
    canvas: {
      width: 842,
      height: 595,
      orientation: 'landscape',
      background: {
        type: 'gradient',
        gradient: {
          type: 'linear',
          angle: 135,
          colors: [
            { color: '#667EEA', position: 0 },
            { color: '#764BA2', position: 100 }
          ]
        }
      },
      border: {
        width: 0,
        style: 'solid',
        color: 'transparent',
        radius: 0
      },
      padding: 40
    },
    elements: [
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'White Card',
        shapeType: 'rectangle',
        fill: '#FFFFFF',
        border: { width: 0, style: 'solid', color: 'transparent', radius: 16 },
        position: { x: 60, y: 40, width: 722, height: 515, zIndex: 1 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Top Color Bar',
        shapeType: 'rectangle',
        fill: '#F59E0B',
        border: { width: 0, style: 'solid', color: 'transparent', radius: 0 },
        position: { x: 60, y: 40, width: 722, height: 12, zIndex: 2 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Certificate Title',
        content: 'CERTIFICATE',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 40,
          fontWeight: 'bold',
          letterSpacing: 4,
          color: '#667EEA'
        },
        position: { x: 221, y: 80, width: 400, height: 55, zIndex: 3 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Of Achievement',
        content: 'of Achievement',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 16,
          fontWeight: '500',
          color: '#9CA3AF'
        },
        position: { x: 321, y: 130, width: 200, height: 25, zIndex: 4 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Presented To',
        content: 'Proudly presented to',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 12,
          color: '#6B7280'
        },
        position: { x: 321, y: 180, width: 200, height: 20, zIndex: 5 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Recipient Name',
        variableKey: 'holder_name',
        placeholder: 'John Doe',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 36,
          fontWeight: 'bold',
          color: '#1F2937'
        },
        position: { x: 171, y: 205, width: 500, height: 50, zIndex: 6 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SHAPE,
        name: 'Underline',
        shapeType: 'rectangle',
        fill: '#F59E0B',
        border: { width: 0, style: 'solid', color: 'transparent', radius: 2 },
        position: { x: 296, y: 258, width: 250, height: 4, zIndex: 7 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'For Completing',
        content: 'For completing the course',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 12,
          color: '#6B7280'
        },
        position: { x: 296, y: 280, width: 250, height: 20, zIndex: 8 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Course Title',
        variableKey: 'course_title',
        placeholder: 'Creative Design Fundamentals',
        format: 'text',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 20,
          fontWeight: '600',
          color: '#374151'
        },
        position: { x: 171, y: 305, width: 500, height: 35, zIndex: 9 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.VARIABLE,
        name: 'Issue Date',
        variableKey: 'issued_date',
        placeholder: 'January 15, 2024',
        format: 'date',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 12,
          fontWeight: '500',
          color: '#6B7280'
        },
        position: { x: 130, y: 470, width: 150, height: 20, zIndex: 10 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.TEXT,
        name: 'Date Label',
        content: 'Date',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: 10,
          color: '#9CA3AF'
        },
        position: { x: 130, y: 490, width: 150, height: 18, zIndex: 11 },
        locked: false,
        visible: true,
        opacity: 1
      },
      {
        id: el(),
        type: CanvasElementType.SIGNATURE,
        name: 'Signature',
        signatureType: 'text',
        text: 'Emily Chen',
        showLabel: true,
        label: 'Course Instructor',
        style: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: '"Dancing Script", cursive',
          fontSize: 24,
          color: '#374151'
        },
        position: { x: 546, y: 450, width: 200, height: 70, zIndex: 12 },
        locked: false,
        visible: true,
        opacity: 1
      }
    ],
    gridEnabled: true,
    snapToGrid: true,
    gridSize: 10
  }
};

/**
 * Blank template for starting from scratch
 */
export const blankTemplate: TemplatePreset = {
  id: 'blank',
  name: 'Create from Blank',
  description: 'Start with a clean canvas and design from scratch',
  category: 'minimalist',
  tags: ['blank', 'empty', 'custom'],
  layout: {
    version: '1.0.0',
    canvas: {
      width: 842,
      height: 595,
      orientation: 'landscape',
      background: {
        type: 'solid',
        color: '#FFFFFF'
      },
      border: {
        width: 1,
        style: 'solid',
        color: '#E5E7EB',
        radius: 0
      },
      padding: 40
    },
    elements: [],
    gridEnabled: true,
    snapToGrid: true,
    gridSize: 10
  }
};

/**
 * All available template presets
 */
export const TEMPLATE_PRESETS: TemplatePreset[] = [
  blankTemplate,
  elegantGoldTemplate,
  modernBlueTemplate,
  minimalistWhiteTemplate,
  classicVintageTemplate,
  professionalCorporateTemplate,
  colorfulAchievementTemplate
];

/**
 * Get template preset by ID
 */
export function getTemplatePresetById(id: string): TemplatePreset | undefined {
  return TEMPLATE_PRESETS.find(preset => preset.id === id);
}

/**
 * Get template presets by category
 */
export function getTemplatePresetsByCategory(category: TemplatePreset['category']): TemplatePreset[] {
  return TEMPLATE_PRESETS.filter(preset => preset.category === category);
}

/**
 * Search template presets
 */
export function searchTemplatePresets(query: string): TemplatePreset[] {
  const lowerQuery = query.toLowerCase();
  return TEMPLATE_PRESETS.filter(preset =>
    preset.name.toLowerCase().includes(lowerQuery) ||
    preset.description.toLowerCase().includes(lowerQuery) ||
    preset.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
}
